// EditEmail.js
import React, { useEffect, useState, useRef } from "react";
import "./Email.css";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Container from "@mui/material/Container";
import { useLocation } from "react-router-dom";
import axios from "axios";
import { useConfigs } from "../../configuration/useConfigs";
import dayjs from "dayjs";
import timezone from "dayjs/plugin/timezone"; // Add this import
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import DeleteIcon from "@mui/icons-material/Delete";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/Edit";
import TextField from "@mui/material/TextField";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Divider from "@mui/material/Divider";
import CircularProgress from "@mui/material/CircularProgress";
import Fab from "@mui/material/Fab"; // Added import for Fab
dayjs.extend(timezone);
dayjs.tz.setDefault("America/Chicago"); // Set default to CST

export default function EditEmail() {
 const location = useLocation();
 const [items, setItems] = useState([]);
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);
 const { config } = useConfigs();
 const fallbackSrc = `${config.STORAGE_CONTAINER_URL}/market-launch/images/emails/global/flavoriq.png`;
 const [openDialog, setOpenDialog] = useState(false);
 const [deleteIndex, setDeleteIndex] = useState(null);
 const itemRefs = useRef([]);
 const [editing, setEditing] = useState({}); // { [index_field]: true }
 const [useDefaultImages, setUseDefaultImages] = useState([]);
 const [headerLogos, setHeaderLogos] = useState({
  griffith: false,
  customCulinary: false,
  terova: false,
  nourish: false,
 });
 const [emailHeadline, setEmailHeadline] = useState("Celebrating many successful launches across Griffith Foods");
 const [croppedFailed, setCroppedFailed] = useState([]);
 const [showTopBtn, setShowTopBtn] = useState(false); // Added state for back to top button visibility

 const griffithSrc = `${config.STORAGE_CONTAINER_URL}${config.EMAIL_LOGO_GRIFFITH}`;
 const customCulinarySrc = `${config.STORAGE_CONTAINER_URL}${config.EMAIL_LOGO_CC}`;
 const terovaSrc = `${config.STORAGE_CONTAINER_URL}${config.EMAIL_LOGO_TEROVA}`;
 const nourishSrc = `${config.STORAGE_CONTAINER_URL}${config.EMAIL_LOGO_NOURISH}`;
 

 const handleLogoChange = (event) => {
  setHeaderLogos({
   ...headerLogos,
   [event.target.name]: event.target.checked,
  });
 };

 const toggleUseDefault = (index) => {
  setUseDefaultImages((prev) => {
   const updated = [...prev];
   updated[index] = !updated[index];
   return updated;
  });
 };

 const startEdit = (index, field) => {
  setEditing({ ...editing, [`${index}_${field}`]: true });
 };

 const saveEdit = (index, field, value) => {
  const updatedItems = [...items];
  if (field === 'conceptName') {
   updatedItems[index].conceptName = value;
  } else if (field === 'regions') {
   updatedItems[index].regionCountriesLongNames = value.split(',').map(r => r.trim());
  } else if (field === 'solutions') {
   const solutionsArray = value.split(',').map(s => s.trim());
   updatedItems[index].productCat1Description = solutionsArray[0] || '';
   updatedItems[index].productCat2Description = solutionsArray[1] || '';
   updatedItems[index].productCat3Description = solutionsArray[2] || '';
   updatedItems[index].productCat4Description = solutionsArray[3] || '';
  }
  setItems(updatedItems);
  setEditing({ ...editing, [`${index}_${field}`]: false });
 };

 const handleKeyDown = (e, index, field, value) => {
  if (e.key === 'Enter') {
   saveEdit(index, field, value);
  }
 };

 const handleDeleteClick = (index) => {
  setDeleteIndex(index);
  setOpenDialog(true);
 };

 const handleCloseDialog = () => {
  setOpenDialog(false);
  setDeleteIndex(null);
 };

 const handleConfirmDelete = () => {
  if (deleteIndex !== null) {
   const updatedItems = [...items];
   updatedItems.splice(deleteIndex, 1);
   setItems(updatedItems);
   setUseDefaultImages((prev) => prev.filter((_, i) => i !== deleteIndex));
   setCroppedFailed((prev) => prev.filter((_, i) => i !== deleteIndex));
  }
  handleCloseDialog();
 };

 const handleMoveUp = (index) => {
  if (index === 0) return;
  const updatedItems = [...items];
  [updatedItems[index - 1], updatedItems[index]] = [updatedItems[index], updatedItems[index - 1]];
  setItems(updatedItems);
  setUseDefaultImages((prev) => {
   const updated = [...prev];
   [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
   return updated;
  });
  setCroppedFailed((prev) => {
   const updated = [...prev];
   [updated[index - 1], updated[index]] = [updated[index], updated[index - 1]];
   return updated;
  });
  setTimeout(() => {
   itemRefs.current[index - 1]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
 };

 const handleMoveDown = (index) => {
  if (index === items.length - 1) return;
  const updatedItems = [...items];
  [updatedItems[index], updatedItems[index + 1]] = [updatedItems[index + 1], updatedItems[index]];
  setItems(updatedItems);
  setUseDefaultImages((prev) => {
   const updated = [...prev];
   [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
   return updated;
  });
  setCroppedFailed((prev) => {
   const updated = [...prev];
   [updated[index], updated[index + 1]] = [updated[index + 1], updated[index]];
   return updated;
  });
  setTimeout(() => {
   itemRefs.current[index + 1]?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, 0);
 };

 const truncateText = (text, maxLength) => {
  if (text.length > maxLength) {
    return text.substring(0, maxLength) + '...';
  }
  return text;
 };

 const handleSubmit = () => {
  const selectedLogos = [];
  if (headerLogos.griffith) selectedLogos.push(griffithSrc);
  if (headerLogos.customCulinary) selectedLogos.push(customCulinarySrc);
  if (headerLogos.terova) selectedLogos.push(terovaSrc);
  if (headerLogos.nourish) selectedLogos.push(nourishSrc);

  const data = {
   Headline: emailHeadline,
   Logos: selectedLogos,
   SubHeadline: config.EMAIL_SUBHEADLINE,
   IntroText: config.EMAIL_INTRO_TEXT,
   FeatureImage: config.EMAIL_FEATURE_IMAGE,
   Items: items.map((item, index) => {
    const regionsString = item.regionCountriesLongNames.join(", ");
    const solutionsString = [
      item.productCat1Description?.trim() || '',
      item.productCat2Description?.trim() || '',
      item.productCat3Description?.trim() || '',
      item.productCat4Description?.trim() || ''
    ].filter(Boolean).join(", ");

    return ({
    ConceptName: truncateText(item.conceptName || '', 47),
    ChefName: item.chefNavigation?.chefName || '',
    ChefPhoto: config.CHEF_PHOTOS_URL_PREFIX + (item.chefNavigation?.chefPhoto || ''),
    Regions: truncateText(regionsString, 84),
    Solutions: truncateText(solutionsString, 84),
    ShipToSegment: item.segmentNavigation?.name || '',
    ShipToBrand: item.brandNavigation?.brandName || item.brandOtherText || '',
    ShipToBrandLogo: item.brandNavigation?.brandLogo ? config.BRANDS_URL_PREFIX + item.brandNavigation.brandLogo : '',
    EndCustomerSegment: item.segmentEndNavigation?.name || '',
    EndCustomerBrand: item.brandEndNavigation?.brandName || item.brandEndOtherText || '',
    EndCustomerBrandLogo: item.brandEndNavigation?.brandLogo ? config.BRANDS_URL_PREFIX + item.brandEndNavigation.brandLogo : '',
    Image: useDefaultImages[index] || !item.blobImageName ? fallbackSrc : (croppedFailed[index] ? `${config.STORAGE_CONTAINER_URL}/market-launch/images/concepts/${dayjs(item.creationDate).format("YYYY/MMM").toLowerCase()}/${item.blobImageName}` : `${config.STORAGE_CONTAINER_URL}/market-launch/images/concepts/${dayjs(item.creationDate).format("YYYY/MMM").toLowerCase()}/${item.blobImageName ? item.blobImageName.replace(/\.[^/.]+$/, "") : ''}_crop.jpg`),
   });
  })
  };

 // console.log('Submitting payload:', JSON.stringify(data, null, 2));

  axios.post("/api/EmailGen/GenerateHtml", data)
   .then(response => {
    console.log("payload:: ", JSON.stringify(response.data))
    const newWindow = window.open("", "_blank","HTMLEmail");
    newWindow.document.write(response.data.html);
    newWindow.document.close();
   })
   .catch(err => {
    console.error('API Error:', err);
    if (err.response) {
      console.error('Response data:', err.response.data);
      console.error('Response status:', err.response.status);
      console.error('Response headers:', err.response.headers);
    } else if (err.request) {
      console.error('No response received:', err.request);
    } else {
      console.error('Error setting up request:', err.message);
    }
   });
 };

 useEffect(() => {
  const selectedIds = location.state?.selectedIds || [];
  if (selectedIds.length === 0) {
   setError("No items selected.");
   setLoading(false);
   return;
  }

  axios
   .post("/api/EmailGen/GetConceptItems", selectedIds)
   .then((response) => {
    setItems(response.data);
    setUseDefaultImages(new Array(response.data.length).fill(false));
    setCroppedFailed(new Array(response.data.length).fill(false));
    setLoading(false);
   // console.log("response:: ", JSON.stringify(response.data));
   })
   .catch((err) => {
    console.error("Error fetching items:", err);
    setError("Failed to load items.");
    setLoading(false);
   });
 }, [location.state]);

 // Added useEffect for scroll handling
 useEffect(() => {
  const handleScroll = () => {
   setShowTopBtn(window.scrollY > 300); // Show button after scrolling 300px
  };
  window.addEventListener("scroll", handleScroll);
  return () => window.removeEventListener("scroll", handleScroll);
 }, []);

 // Define the cycling colors
 const colors = ["#a0c800", "#930a93", "#ff9300", "#00aaf0", "#007044"];

 return (
  <Container
   disableGutters
   sx={{
    width: "640px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    mt: 0,
    mb: 2,
    backgroundColor: "#fff"
   }}
   fixed>
   <Box sx={{ mt: 4, mb: 2, width: 640 }}>
    <Typography variant="h4" align="center">
     Prep Your Email
    </Typography>
    <Typography align="center" sx={{color: "#464646", fontSize: "16px", mt: 2}}>
        Customize portions of your email below and submit. The contents for your email will be generated for you to copy and paste into Outlook Mail.
    </Typography>
    
    <Divider textAlign="left" sx={{ mt: 3 }}>Main Header Region</Divider>
 
    <Typography align="left" sx={{ mt: 3, color: "#464646", fontSize: "14px", fontWeight: 500, lineHeight: 1 }}>
     Choose logos to include:
    </Typography>
    <Box sx={{ display: 'flex', justifyContent: 'space-around', width: '100%', mt: 1, mb: 1}}>
     <FormControlLabel
      control={<Checkbox checked={headerLogos.griffith} onChange={handleLogoChange} name="griffith" />}
      label={<img src={griffithSrc} alt="Griffith" style={{ height: 25, width: 98 }} />}
     />
     <FormControlLabel
      control={<Checkbox checked={headerLogos.customCulinary} onChange={handleLogoChange} name="customCulinary" />}
      label={<img src={customCulinarySrc} alt="Custom Culinary" style={{ height: 25, width: 98 }} />}
     />
     <FormControlLabel
      control={<Checkbox checked={headerLogos.terova} onChange={handleLogoChange} name="terova" />}
      label={<img src={terovaSrc} alt="Terova" style={{ height: 25, width: 98 }} />}
     />
     <FormControlLabel
      control={<Checkbox checked={headerLogos.nourish} onChange={handleLogoChange} name="nourish" />}
      label={<img src={nourishSrc} alt="Nourish" style={{ height: 25, width: 98 }} />}
     />
    </Box>
    <TextField
     label="Email Headline:"
     fullWidth
     sx={{ mt: 3, mb: 4 }}
     value={emailHeadline}
     onChange={(e) => setEmailHeadline(e.target.value)}
    />

    <Divider textAlign="left" sx={{ mb: 2 }}>
        Product Launch Items
    </Divider>
     <Typography align="left" display="inline" sx={{color: "#464646", fontSize: "16px"}}>
         Click <EditIcon fontSize="small" sx={{width: ".8em", height: ".8em"}} /> to customize copy for product launch name, launched in region/country, and solutions.
    </Typography> <Typography align="left" display="inline" sx={{color: "#464646", fontSize: "16px", fontStyle: "italic"}}>
         Limit copy to two lines to avoid truncation (indicated by an appended "..."). 
    </Typography>
    <Typography align="left" sx={{color: "#464646", fontSize: "16px", mt: 2, mb: 1 }}>
         You can also replace a launch image with the default, move position of launch items <ArrowUpwardIcon fontSize="small" sx={{mb: "-4px"}} /> <ArrowDownwardIcon fontSize="small" sx={{mb: "-4px"}} />, and remove items <DeleteIcon fontSize="small" sx={{mb: "-4px"}} />. 
    </Typography>
   </Box>

   {loading ? (
    <Box sx={{ display: "flex", justifyContent: "center", mt: 2, minHeight: "200px" }}>
     <CircularProgress size={24} />
    </Box>
   ) : (
    <>
     {items.map((item, index) => {
      const itemColor = colors[index % 5];
      const isLeftText = index % 2 === 0; // Alternate: even index text left, odd image left
      const launchDate = dayjs(item.creationDate).format("YYYY/MMM").toLowerCase();
      const solutions = [
        item.productCat1Description?.trim(),
        item.productCat2Description?.trim(),
        item.productCat3Description?.trim(),
        item.productCat4Description?.trim()
      ].filter(Boolean).join(', ');

      const isFirst = index === 0;
      const isLast = index === items.length - 1;
      const isSingle = items.length === 1;

      const hasCustomImage = !!item.blobImageName;
      const originalSrc = hasCustomImage ? `${config.STORAGE_CONTAINER_URL}/market-launch/images/concepts/${launchDate}/${item.blobImageName}` : fallbackSrc;
      const croppedSrc = hasCustomImage ? `${config.STORAGE_CONTAINER_URL}/market-launch/images/concepts/${launchDate}/${item.blobImageName.replace(/\.[^/.]+$/, "")}_crop.jpg` : fallbackSrc;
      const isUsingDefault = !hasCustomImage || useDefaultImages[index];
      const currentSrc = isUsingDefault ? fallbackSrc : (croppedFailed[index] ? originalSrc : croppedSrc);
      const buttonText = !hasCustomImage ? "Default Image Only" : (useDefaultImages[index] ? "Use Launch Image" : "Use Default Image");

      return (
       <React.Fragment key={index}>
        
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "600px" }}>
         <Box>
          <Typography variant="h6" align="left" display="inline" sx={{color: "#464646", fontSize: "18px", verticalAlign: "middle"}}>
          #{index + 1}
         </Typography>
         <Button
           variant="filled"
           sx={{color: "#464646"}}
           startIcon={<DeleteIcon />}
           onClick={() => handleDeleteClick(index)}
          >
           Remove Item
          </Button>
         </Box>
         <Box>
          <IconButton
           onClick={() => handleMoveUp(index)}
           disabled={isFirst || isSingle}
           aria-label="move up"
           sx={{color: "#464646"}}
          >
           <ArrowUpwardIcon />
          </IconButton>
          <IconButton
           onClick={() => handleMoveDown(index)}
           disabled={isLast || isSingle}
           aria-label="move down"
           sx={{color: "#464646"}}
          >
           <ArrowDownwardIcon />
          </IconButton>        
         </Box>
        </Box>
        <Box className="mlitem" sx={{ borderTop: `6px solid ${itemColor}` }} ref={(el) => (itemRefs.current[index] = el)}>
         <Box sx={{ display: "flex", mt: 1, textAlign: "center", padding: "14px 0" }}>
          <Box
           sx={{
            flex: 1,
            padding: "14px 5px",
            order: isLeftText ? 1 : 2,
           }}>
           <Box sx={{ position: "relative" }}>
            <IconButton
             sx={{ position: "absolute", [isLeftText ? 'left' : 'right']: -40, top: -5, color: "#464646" }}
             size="small"
             onClick={() => startEdit(index, 'conceptName')}
             
            >
             <EditIcon fontSize="small" />
            </IconButton>
            {editing[`${index}_conceptName`] ? (
             <TextField
              autoFocus
              defaultValue={item.conceptName}
              onKeyDown={(e) => handleKeyDown(e, index, 'conceptName', e.target.value)}
              onBlur={(e) => saveEdit(index, 'conceptName', e.target.value)}
              multiline
              minRows={2}
              sx={{ 
                width: "100%", 
                "& .MuiInputBase-root": { height: "auto" }, 
                color: "#464646",
                "& .MuiInputBase-input": { resize: "vertical" } 
              }}
             />
            ) : (
             <Typography className="mlitem--name" sx={{ paddingBottom: "5px", color: itemColor, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", cursor: "pointer", mb: 1 }} onClick={() => startEdit(index, 'conceptName')}>
              {item.conceptName}
             </Typography>
            )}
           </Box>

           <Typography className="mlitem--subhead" sx={{ margin: "7px 0 0" }}>
            Project Lead
           </Typography>
           <Typography className="mlitem--text" sx={{ color: itemColor }}>
            {item.chefNavigation?.chefName}
           </Typography>
           <img src={config.CHEF_PHOTOS_URL_PREFIX + item.chefNavigation?.chefPhoto} alt={item.chefNavigation?.chefName} style={{ width: 40, height: 40, marginTop: 3, marginBottom: 7 }} />

           <Typography className="mlitem--subhead" sx={{ margin: 0 }}>
            LAUNCHED IN REGION / COUNTRY
           </Typography>

           <Box sx={{ position: "relative" }}>
            <IconButton
             sx={{ position: "absolute", [isLeftText ? 'left' : 'right']: -40, top: -5, color: "#464646" }}
             size="small"
             onClick={() => startEdit(index, 'regions')}
            >
             <EditIcon fontSize="small" />
            </IconButton>
            {editing[`${index}_regions`] ? (
             <TextField
              autoFocus
              defaultValue={item.regionCountriesLongNames.join(", ")}
              onKeyDown={(e) => handleKeyDown(e, index, 'regions', e.target.value)}
              onBlur={(e) => saveEdit(index, 'regions', e.target.value)}
              multiline
              minRows={2}
              sx={{ 
                width: "100%", 
                "& .MuiInputBase-root": { height: "auto" }, 
                color: "#464646",
                "& .MuiInputBase-input": { resize: "vertical" } 
              }}
             />
            ) : (
             <Typography className="mlitem--text" sx={{ marginBottom: "15px", color: itemColor, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", cursor: "pointer" }} onClick={() => startEdit(index, 'regions')}>
              {item.regionCountriesLongNames.map(name => name.trim()).join(", ")}
             </Typography>
            )}
           </Box>

           <Typography className="mlitem--subhead" sx={{ margin: 0 }}>
            Our Solutions
           </Typography>
           <Box sx={{ position: "relative" }}>
            <IconButton
             sx={{ position: "absolute", [isLeftText ? 'left' : 'right']: -40, top: -5, color: "#464646" }}
             size="small"
             onClick={() => startEdit(index, 'solutions')}
            >
             <EditIcon fontSize="small" />
            </IconButton>
            {editing[`${index}_solutions`] ? (
             <TextField
              autoFocus
              defaultValue={solutions}
              onKeyDown={(e) => handleKeyDown(e, index, 'solutions', e.target.value)}
              onBlur={(e) => saveEdit(index, 'solutions', e.target.value)}
              multiline
              minRows={2}
              sx={{ 
                width: "100%", 
                "& .MuiInputBase-root": { height: "auto" },
                "& .MuiInputBase-input": { resize: "vertical" } 
              }}
             />
            ) : (
             <Typography className="mlitem--text" sx={{ color: itemColor, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden", cursor: "pointer" }} onClick={() => startEdit(index, 'solutions')}>
              {solutions}
             </Typography>
            )}
           </Box>
          </Box>

          <Box sx={{ position: "relative", flex: 1, display: "flex", justifyContent: isLeftText ? "flex-end" : "flex-start", alignItems: "flex-start", order: isLeftText ? 2 : 1 }}>
           <img 
            src={currentSrc} 
            alt={item.conceptName} 
            style={{ maxWidth: "300px", height: "300px" }} 
            onError={(e) => {
              if (!isUsingDefault && !croppedFailed[index]) {
                setCroppedFailed((prev) => {
                  const newFailed = [...prev];
                  newFailed[index] = true;
                  return newFailed;
                });
              }
            }}
           />
           <Button
            variant="outlined"
            size="small"
            disabled={!hasCustomImage}
            onClick={() => toggleUseDefault(index)}
            sx={{ 
              position: "absolute",
              [isLeftText ? 'right' : 'left']: -150,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 140 
            }}
           >
            {buttonText}
           </Button>
          </Box>
         </Box>

         <Box sx={{ display: "flex", width: "100%", backgroundColor: "#eeeeee", padding: "18px 0 0" }}>
          <Box sx={{ flex: 1, textAlign: "center", marginTop: "7px" }}>
           <Typography className="mlitem--subhead" sx={{ fontSize: "16px !important" }}>
            Ship-To Customer
           </Typography>
           <Typography className="mlitem--subhead" display="inline" sx={{ fontWeight: "400 !important" }}>
            Segment:
           </Typography>{" "}
           <Typography className="mlitem--text" display="inline" sx={{ color: itemColor }}>
            {item.segmentNavigation?.name}
           </Typography>
           <br />
           <Typography className="mlitem--subhead" display="inline" sx={{ paddingBottom: "2px", fontWeight: "400 !important" }}>
            Brand:
           </Typography>{" "}
           <Typography className="mlitem--text" display="inline" sx={{ color: itemColor }}>
            {item.brandNavigation?.brandName ? item.brandNavigation.brandName : item.brandOtherText}
           </Typography>
           <br />
           <img src={item.brandNavigation?.brandLogo?.length > 0 ? config.BRANDS_URL_PREFIX + item.brandNavigation.brandLogo : '/images/blank.png'} style={{ width: "50px", height: "50px" }} />
          </Box>
          <Box sx={{ flex: 1, textAlign: "center", marginTop: "7px" }}>
           <Typography className="mlitem--subhead" sx={{ fontSize: "16px !important" }}>
            End Customer
           </Typography>
           <Typography className="mlitem--subhead" display="inline" sx={{ fontWeight: "400 !important" }}>
            Segment:
           </Typography>{" "}
           <Typography className="mlitem--text" display="inline" sx={{ color: itemColor }}>
            {item.segmentEndNavigation?.name}
           </Typography>
           <br />
           <Typography className="mlitem--subhead" display="inline" sx={{ paddingBottom: "2px", fontWeight: "400 !important" }}>
            Brand:
           </Typography>{" "}
           <Typography className="mlitem--text" display="inline" sx={{ paddingBottom: "2px", color: itemColor }}>
            {item.brandEndNavigation?.brandName ? item.brandEndNavigation.brandName : item.brandEndOtherText}
           </Typography>
           <br />
           <img src={item.brandEndNavigation?.brandLogo?.length > 0 ? config.BRANDS_URL_PREFIX + item.brandEndNavigation.brandLogo : '/images/blank.png'} style={{ width: "50px", height: "50px" }} />
          </Box>
         </Box>
        </Box>
       </React.Fragment>
      );
     })}
     <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
      <Button variant="contained" onClick={handleSubmit}>
       Generate Email HTML
      </Button>
     </Box>
    <Box sx={{ width: 600, p: 2, mb: 8, mt:1, backgroundColor: '#f0f0f0', borderRadius: 2}}>
    <Typography align="left" sx={{color: "#464646", fontSize: "16px" }}>    
     <b>To copy the contents into Outlook:</b><br/><br/>
     <ol style={{ paddingLeft: '20px', margin: 0 }}>
      <li>Click the <b>Generate Email HTML</b> button and wait for a new window to open showing the generated email.</li>
      <li><b>Command+A</b> on Mac (<b>Ctrl+A</b> on Windows) to select all content.</li>
      <li><b>Command+C</b> on Mac (<b>Ctrl+C</b> on Windows) to copy.</li>
      <li><b>Create a new email message</b>, click in the body area of the email, <b>right click and select paste</b> or <b>Command+V/Ctrl+V</b> to paste.</li>
     </ol>
     <br/>
     If the email content doesn't render well, ensure Outlook is set to compose in <b>HTML</b> format. You can check this by going to <b>Settings &gt; Mail</b>. Verify the following options are set: <b>"Compose messages in <i>HTML</i> format"</b> and <b>Cut, Copy, Paste</b> options are set to <b>"Keep source formatting"</b>. 
    </Typography>
   </Box>
    </>
   )}

   <Dialog
    open={openDialog}
    onClose={handleCloseDialog}
    aria-labelledby="alert-dialog-title"
    aria-describedby="alert-dialog-description"
   >
    <DialogTitle id="alert-dialog-title" color="primary">{"Delete Product Launch Item"}</DialogTitle>
    <DialogContent>
     <DialogContentText id="alert-dialog-description">
      Are you sure you want to delete this item?
     </DialogContentText>
    </DialogContent>
    <DialogActions>
     <Button onClick={handleCloseDialog} color="secondary">
      Cancel
     </Button>
     <Button onClick={handleConfirmDelete} color="primary" autoFocus>
      Confirm
     </Button>
    </DialogActions>
   </Dialog>
   {showTopBtn && ( // Added back to top button
    <Fab
     color="#464646"
     aria-label="back to top"
     sx={{ position: 'fixed', bottom: 16, right: 32 }} 
     onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
    >
     <ArrowUpwardIcon fontSize="small"/>
    </Fab>
   )}
  </Container>
 );
}